import { types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { PeriodDataset, initialState as emptyPeriodChart } from "./period-chart";

export const ComposedChartDataset = types
  .model({
    charts: types.array(PeriodDataset),
    period: types.union(types.literal("Quarter"), types.literal("Year")),
  })
  .actions((self) => ({
    load: flow(function* (silent = false) {
      const tasks = self.charts.map((c) => c.load(silent));
      yield Promise.all(tasks);
    }),

    setLoaded(value: boolean) {
      self.charts.forEach((c) => c.setLoaded(value));
    },

    setPeriod(value: "Quarter" | "Year") {
      self.charts.forEach((c) => c.setPeriod(value));
      self.period = value;
    },
  }))
  .views((self) => ({
    get loading() {
      return self.charts.some((c) => c.loading);
    },

    get loaded() {
      return self.charts.every((c) => c.loaded);
    },

    get amChartData() {
      // Получаем все datasets для текущего периода
      const datasets = self.charts.map((chart) => chart.data.get(self.period));

      // Находим dataset с наибольшим количеством labels
      const longestDataset = datasets
        .filter((src): src is any => !!src && Array.isArray(src.labels) && Array.isArray(src.data))
        .reduce((longest, current) => (current.labels.length > longest.labels.length ? current : longest), {
          labels: [],
          data: [],
        } as any);

      // Если нет данных, вернуть пустой массив
      if (longestDataset.labels.length === 0) {
        return [];
      }

      // Использовать порядок labels из dataset с наибольшим количеством элементов
      return longestDataset.labels.map((period: any) => {
        const datum: TStringMap<any> = { period };
        let max = 0;

        self.charts.forEach((chart, i) => {
          const src = chart.data.get(self.period);
          let value = 0;

          if (src && Array.isArray(src.labels) && Array.isArray(src.data)) {
            const index = src.labels.indexOf(period);
            value = index < 0 ? 0 : src.data[index];
          }

          datum[`data${i}`] = value;
          if (value > max) {
            max = value;
          }
        });

        datum.max = max;
        return datum;
      });
    },
  }))
  .named("ComposedChartDataset");

export type ComposedChartDatasetType = typeof ComposedChartDataset.Type;
export type ComposedChartDatasetSnapshotType = typeof ComposedChartDataset.SnapshotType;

export const initialState = (charts: { url: string; title: string }[]): ComposedChartDatasetSnapshotType => {
  return {
    charts: charts.map(({ title, url }) => emptyPeriodChart(url, title)),
    period: "Quarter",
  };
};
